import { Box, Chip, Paper, Tooltip } from '@mui/material';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import Alert from '@components/Alert';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import IconButton from '@components/IconButton';

import { DISCERN_JOBS_CURATE } from '@constants/routes';

import { useNavigate } from '@hooks/useNavigate';
import { useSnackbar } from '@hooks/useSnackbar';

import { mapBBoxTitleToFormType } from '@utils/mapBBoxTitleToFormType';
import {
  resolveDiscernGroupIndex,
  sortDiscernResults,
} from '@utils/sortDiscernResults';

import type { GetDiscernDocumentQuery } from 'generated/discern.generated';
import { useUpdateDiscernResultStatus } from 'generated/discern.generated';
import { CurationStatus, DiscernResultStatus } from 'generated/types';

const messages = defineMessages({
  noResources: {
    id: 'discern.jobDrilldown.noResources',
    defaultMessage: 'No resources detected for this job.',
  },
  title: {
    id: 'discern.jobDrilldown.title',
    defaultMessage: 'Discern Job',
  },
  jobProcessing: {
    id: 'discern.jobDrilldown.jobProcessing',
    defaultMessage: 'Job is still in progress. Try again later.',
  },
  jobComplete: {
    id: 'discern.jobDrilldown.jobComplete',
    defaultMessage: 'This job is already completed.',
  },
  jobDeleted: {
    id: 'discern.jobDrilldown.jobDeleted',
    defaultMessage: 'This job has been deleted and cannot be accessed.',
  },
  pageSingular: {
    id: 'discern.jobDrilldown.pageSingular',
    defaultMessage: 'Page {range}',
  },
  pagePlural: {
    id: 'discern.jobDrilldown.pagePlural',
    defaultMessage: 'Pages {range}',
  },
  jobFailed: {
    id: 'discern.jobDrilldown.jobFailed',
    defaultMessage:
      'Processing failed for this document. It will not complete — no further updates are expected.',
  },
});

/** Same resolution rules as sorting — prefer group_index, else parse page_group. */
export const resolveDisplayGroupIndex = resolveDiscernGroupIndex;

/**
 * Format a page-group's original document pages, e.g. [8, 9, 10] -> "Pages 8–10",
 * [2, 4, 7] -> "Pages 2, 4, 7". Tolerates malformed runtime values without throwing.
 */
export function formatPageRange(
  pages: unknown,
  formatMessage: IntlShape['formatMessage']
): string {
  if (!Array.isArray(pages) || pages.length === 0) {
    return '—';
  }

  const numericPages = pages.filter(
    (page): page is number => typeof page === 'number' && Number.isFinite(page)
  );
  if (numericPages.length === 0) {
    return '—';
  }

  const sorted = [...numericPages].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i < sorted.length; i += 1) {
    const curr = sorted[i];
    if (curr === prev + 1) {
      prev = curr;
      continue;
    }
    ranges.push(start === prev ? `${start}` : `${start}–${prev}`);
    start = curr;
    prev = curr;
  }
  ranges.push(start === prev ? `${start}` : `${start}–${prev}`);

  const range = ranges.join(', ');
  return formatMessage(
    sorted.length === 1 ? messages.pageSingular : messages.pagePlural,
    { range }
  );
}

/**
 * Resolve the "Document Group/Pages" cell for a result. Falls back to the raw
 * page_group label (e.g. "default") when no group can be resolved, the index
 * is out of range, or the selected pageGroups entry is not an array — matching
 * the display used before page-group identity was tracked.
 */
export function resolvePagesDisplay(
  result: { group_index?: number | null; page_group: string },
  pageGroups: unknown[],
  formatMessage: IntlShape['formatMessage']
): string {
  const groupIndex = resolveDisplayGroupIndex(result);
  if (
    groupIndex !== null &&
    groupIndex >= 0 &&
    groupIndex < pageGroups.length
  ) {
    const pages = pageGroups[groupIndex];
    if (!Array.isArray(pages)) {
      return result.page_group;
    }
    return formatPageRange(pages, formatMessage);
  }
  return result.page_group;
}

interface DiscernJobDocumentDrilldownProps {
  document: NonNullable<GetDiscernDocumentQuery['getDiscernDocument']>;
}

const DiscernJobDocumentDrilldown: React.FC<
  DiscernJobDocumentDrilldownProps
> = ({ document }) => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { navigateRoute } = useNavigate();
  const [updateResultStatus] = useUpdateDiscernResultStatus();

  const job = document.job;
  const jobResults = job?.results;

  const pageGroups = useMemo<unknown[]>(() => {
    const groups = document.grouping_state_json.pageGroups;
    return Array.isArray(groups) ? groups : [];
  }, [document.grouping_state_json]);

  // Debug: Log to help diagnose missing results
  React.useEffect(() => {
    if (!jobResults?.length && document.status === 'Structuring') {
      // eslint-disable-next-line no-console
      console.log(
        'DiscernJobDocumentDrilldown: No results found for Structuring document',
        {
          hasJob: !!job,
          hasJobResults: !!jobResults,
          jobResultsType: typeof jobResults,
          jobResultsValue: jobResults,
          documentStatus: document.status,
          pageGroupsCount: pageGroups.length,
        }
      );
    }
  }, [
    document,
    jobResults?.length,
    document.status,
    job,
    pageGroups.length,
    jobResults,
  ]);

  const isDocumentFailed = document.status === CurationStatus.Failed;

  const sortedJobResults = useMemo(
    () => sortDiscernResults(jobResults ?? []),
    [jobResults]
  );

  const tableData = sortedJobResults
    .filter(({ resource_name, status }) => {
      const rawType = resource_name
        ? mapBBoxTitleToFormType(resource_name)
        : null;
      return rawType !== null || status === DiscernResultStatus.Processing;
    })
    .map((result) => ({
      discern_result_id: result.discern_result_id,
      resource: result.resource_name || 'Unknown',
      status: result.status,
      pages: resolvePagesDisplay(result, pageGroups, formatMessage),
    }));

  const handleMarkComplete = async (discernResultId: string) => {
    await updateResultStatus({
      variables: {
        input: {
          discern_result_id: discernResultId,
          status: DiscernResultStatus.Complete,
        },
      },
    });
  };

  const handleRowClick = (
    _rowData: string[],
    { dataIndex }: { dataIndex: number; rowIndex: number }
  ) => {
    const rowData = tableData[dataIndex];
    const status = rowData.status;

    // A Failed document takes precedence over any row's own status (rows can be
    // left behind mid-Processing if the backend's post-failure cleanup didn't
    // finish) — never tell the curator to wait on a document that has already
    // failed and will not update further.
    if (isDocumentFailed) {
      enqueueSnackbar({
        message: formatMessage(messages.jobFailed),
        variant: 'error',
      });
      return;
    }

    // Only navigate to curation if status is not "Processing" and we have bounding boxes
    // Processing rows don't have bounding boxes yet, so we can't navigate to curation

    const statusMessageMap: Record<
      string,
      {
        messageKey: keyof typeof messages;
        variant: 'info' | 'success' | 'error';
      }
    > = {
      Processing: { messageKey: 'jobProcessing', variant: 'info' },
      Complete: { messageKey: 'jobComplete', variant: 'success' },
      Delete: { messageKey: 'jobDeleted', variant: 'error' },
    };

    const statusInfo = statusMessageMap[status];
    if (status in statusMessageMap) {
      enqueueSnackbar({
        message: formatMessage(messages[statusInfo.messageKey]),
        variant: statusInfo.variant,
      });
      return; // Don't navigate for these statuses
    }

    // Navigate to curation view with resource index as query parameter
    navigateRoute(DISCERN_JOBS_CURATE(document.discern_document_id), {
      result_id: rowData.discern_result_id,
    });
  };
  const getStatusChipProps = (status: string) => {
    switch (status) {
      case DiscernResultStatus.Complete:
        return { color: 'success', variant: 'filled' };

      case DiscernResultStatus.Delete:
        return { color: 'error', variant: 'filled' };

      default:
        return { color: 'warning', variant: 'soft' };
    }
  };
  return (
    <Box sx={{ width: '100%' }}>
      {isDocumentFailed && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="job-failed-alert">
          {formatMessage(messages.jobFailed)}
        </Alert>
      )}
      <Paper sx={{ p: 2, width: '100%' }}>
        <MuiDataTable
          columns={[
            {
              name: 'resource',
              className: 'small',
              options: {
                customHeadLabelRender: () => 'Resource/Record Type',
                filter: true,
                sort: true,
                searchable: true,
              },
            },
            {
              name: 'status',
              className: 'small',
              label: 'Status',
              options: {
                customBodyRender: (status: string) => {
                  const { color, variant } = getStatusChipProps(status);

                  return (
                    <Chip
                      label={status}
                      size="small"
                      color={color as any}
                      variant={variant as any}
                    />
                  );
                },
                filter: true,
                sort: true,
                searchable: true,
              },
            },
            {
              name: 'pages',
              className: 'small',
              label: 'Document Group/Pages',
              options: {
                filter: true,
                sort: true,
                searchable: true,
              },
            },
            {
              name: 'actions',
              options: {
                customHeadLabelRender: () => 'Actions',
                customBodyRender: (_value, tableMeta) => {
                  const rowData = tableData[tableMeta.rowIndex];
                  const isProcessing =
                    rowData.status === DiscernResultStatus.Processing;
                  if (isProcessing || isDocumentFailed) {
                    return null;
                  }
                  const isComplete =
                    rowData.status === DiscernResultStatus.Complete;
                  const isDelete =
                    rowData.status === DiscernResultStatus.Delete;
                  return (
                    <Tooltip
                      title={
                        isDelete ? 'Cannot mark complete' : 'Mark complete'
                      }
                    >
                      <span>
                        <IconButton
                          name="circle-check"
                          color={isComplete ? 'success.main' : 'text.secondary'}
                          iconType={isComplete ? 'fas' : 'far'}
                          disabled={isDelete || isComplete}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isDelete) return;
                            handleMarkComplete(rowData.discern_result_id);
                          }}
                        />
                      </span>
                    </Tooltip>
                  );
                },
                filter: false,
                sort: false,
                searchable: false,
              },
            },
          ]}
          data={tableData}
          options={{
            search: true,
            sort: true,
            filter: true,
            onRowClick: handleRowClick,
            textLabels: {
              body: {
                noMatch: formatMessage(messages.noResources),
              },
            },
          }}
        />
      </Paper>
    </Box>
  );
};
export default DiscernJobDocumentDrilldown;
